import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const inp = {
  width: '100%', padding: '0.625rem 0.875rem',
  border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem',
  fontSize: '0.875rem', color: theme.text, background: theme.white,
  outline: 'none', boxSizing: 'border-box',
};

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const EMPTY_MEMBER = { name: '', relation: '', dob: '', aadhar: '' };

const RELATIONSHIP_OPTIONS = [
  'Father', 'Mother', 'Spouse', 'Son', 'Daughter', 'Brother', 'Sister',
  'Grandfather', 'Grandmother', 'Father-in-Law', 'Mother-in-Law',
  'Brother-in-Law', 'Sister-in-Law', 'Uncle', 'Aunt', 'Other',
];
const SKILL_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];

function Field({ label, required, children, fullWidth }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>
        {label} {required && <span style={{ color: theme.danger }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card title={title} style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
        {children}
      </div>
    </Card>
  );
}

// Read-only row for review screen
function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: `1px solid ${theme.border}` }}>
      <span style={{ width: '200px', flexShrink: 0, fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: theme.text, fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

function ReviewSection({ title, children }) {
  return (
    <Card title={title} style={{ marginBottom: '1.25rem' }}>
      {children}
    </Card>
  );
}

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // 'edit' | 'review' | 'done'
  const [step, setStep] = useState('edit');
  const [empId, setEmpId] = useState(null);

  const [form, setForm] = useState({
    full_name: '',
    father_husband_name: '', phone: '', alternate_phone: '', personal_email: '',
    emergency_contact: '',
    dob: '', gender: '', marital_status: '', blood_group: '', qualification: '',
    aadhar_number: '', address: '', present_address: '',
    pan_number: '', bank_ifsc: '', bank_name: '', bank_branch: '', bank_account_number: '',
    pf_uan: '', esic_number: '', esi_hospital: '',
    nominee_name: '', nominee_relation: '', nominee_aadhar: '', nominee_dob: '', nominee_contact: '',
  });

  const [hasPF, setHasPF] = useState('');
  const [hasESI, setHasESI] = useState('');
  const [familyMembers, setFamilyMembers] = useState([{ ...EMPTY_MEMBER }]);

  const [education, setEducation] = useState([]);
  const [newEdu, setNewEdu] = useState({ degree: '', institution: '', field_of_study: '', end_year: '' });

  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState({ skill_name: '', proficiency_level: 'intermediate' });

  const [certifications, setCertifications] = useState([]);
  const [newCert, setNewCert] = useState({ name: '', issuing_org: '', issue_date: '', expiry_date: '' });

  const [experiences, setExperiences] = useState([]);
  const EMPTY_EXP = { company_name: '', designation: '', description: '', from_date: '', to_date: '', is_current: false };
  const [newExp, setNewExp] = useState({ ...EMPTY_EXP });
  const [editingExpId, setEditingExpId] = useState(null);

  const EMPTY_EDU = { degree: '', institution: '', field_of_study: '', end_year: '' };
  const [editingEduId, setEditingEduId] = useState(null);

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [docs, setDocs] = useState({ resume: null, aadhar_copy: null, pan_copy: null, passbook_copy: null });
  const [existingDocs, setExistingDocs] = useState({ resume_path: null, aadhar_copy_path: null, pan_copy_path: null, passbook_copy_path: null });
  const [ifscStatus, setIfscStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileStatus, setProfileStatus] = useState(null);
  const [profileRejectionReason, setProfileRejectionReason] = useState(null);

  useEffect(() => { loadMyProfile(); }, []);

  const loadMyProfile = async () => {
    try {
      const meResp = await import('../../services/api').then(m => m.auth.getMe());
      const empIdVal = meResp.data?.emp_id;
      if (!empIdVal) { setPageLoading(false); return; }
      setEmpId(empIdVal);

      const [empRes, histRes] = await Promise.allSettled([
        hrms.getEmployee(empIdVal),
        hrms.getEmployeeHistory(empIdVal),
      ]);

      if (empRes.status === 'fulfilled') {
        const e = empRes.value.data;
        setForm({
          full_name: e.full_name || '',
          father_husband_name: e.father_husband_name || '',
          phone: e.phone || '',
          alternate_phone: e.alternate_phone || '',
          personal_email: e.personal_email || '',
          dob: e.dob || '',
          gender: e.gender || '',
          marital_status: e.marital_status || '',
          blood_group: e.blood_group || '',
          qualification: e.qualification || '',
          aadhar_number: e.aadhar_number || '',
          address: e.address || '',
          present_address: e.present_address || '',
          pan_number: e.pan_number || '',
          bank_ifsc: e.bank_ifsc || '',
          bank_name: e.bank_name || '',
          bank_branch: e.bank_branch || '',
          bank_account_number: e.bank_account_number || '',
          pf_uan: e.pf_uan || '',
          esic_number: e.esic_number || '',
          esi_hospital: e.esi_hospital || '',
          nominee_name: e.nominee_name || '',
          nominee_relation: e.nominee_relation || '',
          nominee_aadhar: e.nominee_aadhar || '',
          nominee_dob: e.nominee_dob || '',
          nominee_contact: e.nominee_contact || '',
        });
        if (e.bank_name) setIfscStatus('success');
        if (e.profile_photo) setPhotoPreview(getFileUrl(e.profile_photo));
        if (e.profile_status) setProfileStatus(e.profile_status);
        if (e.profile_rejection_reason) setProfileRejectionReason(e.profile_rejection_reason);
        setExistingDocs({
          resume_path: e.resume_path || null,
          aadhar_copy_path: e.aadhar_copy_path || null,
          pan_copy_path: e.pan_copy_path || null,
          passbook_copy_path: e.passbook_copy_path || null,
        });
        if (e.pf_uan) setHasPF('yes');
        if (e.esic_number) setHasESI('yes');
        if (e.family_members) {
          try {
            const parsed = JSON.parse(e.family_members);
            if (Array.isArray(parsed) && parsed.length > 0) setFamilyMembers(parsed);
          } catch {}
        }
      }

      if (histRes.status === 'fulfilled') {
        const h = histRes.value.data;
        setEducation(h.education || []);
        setSkills(h.skills || []);
        setCertifications(h.certifications || []);
        setExperiences(h.experience || []);
      }
    } catch {}
    setPageLoading(false);
  };

  const fetchBankDetails = useCallback(async (ifsc) => {
    if (!ifsc || ifsc.length !== 11) return;
    setIfscStatus('loading');
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`https://ifsc.razorpay.com/${ifsc.toUpperCase()}`, { signal: controller.signal });
      clearTimeout(timeout);
      if (res.ok) {
        const data = await res.json();
        setForm(p => ({ ...p, bank_name: data.BANK || '', bank_branch: data.BRANCH || '' }));
        setIfscStatus('success');
      } else setIfscStatus('error');
    } catch (err) {
      if (err.name === 'AbortError') console.warn('[IFSC] Timed out');
      setIfscStatus('error');
    }
  }, []);

  const handleIfscChange = e => {
    const val = e.target.value.toUpperCase();
    setForm(p => ({ ...p, bank_ifsc: val, bank_name: '', bank_branch: '' }));
    setIfscStatus('');
    if (val.length === 11) fetchBankDetails(val);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (file) { setPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const updateMember = (idx, field, value) =>
    setFamilyMembers(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  // Education
  const saveEducation = async () => {
    if (!newEdu.degree || !newEdu.institution) { alert('Degree and Institution are required.'); return; }
    if (!empId) return;
    const payload = { degree: newEdu.degree, institution: newEdu.institution, field_of_study: newEdu.field_of_study || null, end_year: newEdu.end_year ? Number(newEdu.end_year) : null };
    try {
      if (editingEduId) {
        const res = await hrms.updateEducation(empId, editingEduId, payload);
        setEducation(prev => prev.map(e => e.id === editingEduId ? res.data : e));
        setEditingEduId(null);
      } else {
        const res = await hrms.addEducation(empId, payload);
        setEducation(prev => [...prev, res.data]);
      }
      setNewEdu({ degree: '', institution: '', field_of_study: '', end_year: '' });
    } catch { alert('Failed to save education.'); }
  };

  const editEducation = (edu) => {
    setEditingEduId(edu.id);
    setNewEdu({ degree: edu.degree || '', institution: edu.institution || '', field_of_study: edu.field_of_study || '', end_year: edu.end_year ? String(edu.end_year) : '' });
    setTimeout(() => document.getElementById('edu-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const removeEducation = async (eduId) => {
    if (!empId) return;
    try { await hrms.deleteEducation(empId, eduId); setEducation(prev => prev.filter(e => e.id !== eduId)); }
    catch { alert('Failed to remove education.'); }
  };

  // Skills
  const addSkill = async () => {
    if (!newSkill.skill_name) { alert('Skill name is required.'); return; }
    if (!empId) return;
    try {
      const res = await hrms.addSkill(empId, newSkill);
      setSkills(prev => [...prev, res.data]);
      setNewSkill({ skill_name: '', proficiency_level: 'intermediate' });
    } catch { alert('Failed to add skill.'); }
  };

  const removeSkill = async (skillId) => {
    if (!empId) return;
    try { await hrms.deleteSkill(empId, skillId); setSkills(prev => prev.filter(s => s.id !== skillId)); }
    catch { alert('Failed to remove skill.'); }
  };

  // Certifications
  const addCert = async () => {
    if (!newCert.name) { alert('Certification name is required.'); return; }
    if (!empId) return;
    try {
      const fd = new FormData();
      fd.append('name', newCert.name);
      if (newCert.issuing_org) fd.append('issuing_org', newCert.issuing_org);
      if (newCert.issue_date) fd.append('issue_date', newCert.issue_date);
      if (newCert.expiry_date) fd.append('expiry_date', newCert.expiry_date);
      const res = await hrms.addCertification(empId, fd);
      setCertifications(prev => [...prev, res.data]);
      setNewCert({ name: '', issuing_org: '', issue_date: '', expiry_date: '' });
    } catch { alert('Failed to add certification.'); }
  };

  const removeCert = async (certId) => {
    if (!empId) return;
    try { await hrms.deleteCertification(empId, certId); setCertifications(prev => prev.filter(c => c.id !== certId)); }
    catch { alert('Failed to remove certification.'); }
  };

  // Experience
  const saveExp = async () => {
    if (!newExp.company_name.trim() || !newExp.designation.trim()) { alert('Company name and designation are required.'); return; }
    if (!empId) return;
    const payload = {
      company_name: newExp.company_name.trim(),
      designation: newExp.designation.trim(),
      description: newExp.description.trim() || null,
      from_date: newExp.from_date || null,
      to_date: newExp.is_current ? null : (newExp.to_date || null),
    };
    try {
      if (editingExpId) {
        const res = await hrms.updateExperience(empId, editingExpId, payload);
        setExperiences(prev => prev.map(e => e.id === editingExpId ? { ...e, ...res.data } : e));
        setEditingExpId(null);
      } else {
        const res = await hrms.addExperience(empId, payload);
        setExperiences(prev => [res.data, ...prev]);
      }
      setNewExp({ ...EMPTY_EXP });
    } catch { alert('Failed to save experience.'); }
  };

  const editExp = (exp) => {
    setEditingExpId(exp.id);
    setNewExp({
      company_name: exp.company_name || '',
      designation: exp.designation || '',
      description: exp.description || '',
      from_date: exp.from_date || '',
      to_date: exp.to_date || '',
      is_current: !exp.to_date,
    });
    setTimeout(() => document.getElementById('exp-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const removeExp = async (expId) => {
    if (!empId) return;
    try { await hrms.deleteExperience(empId, expId); setExperiences(prev => prev.filter(e => e.id !== expId)); }
    catch { alert('Failed to remove experience.'); }
  };

  // Phone: exactly 10 digits, must start with 6-9
  const isValidPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(-10);
    return digits.length === 10 && /^[6-9]/.test(digits);
  };

  // PAN format: 5 letters + 4 digits + 1 letter (all uppercase)
  const isValidPAN = (val) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val.toUpperCase());

  // Bank account: 9–18 digits only
  const isValidAccountNumber = (val) => /^\d{9,18}$/.test(val);

  const today = new Date().toISOString().slice(0, 10);

  // Per-file PDF/JPEG validation (10 MB limit)
  const validateDocFile = (file, label) => {
    if (!file) return null;
    if (!['application/pdf', 'image/jpeg'].includes(file.type))
      return `${label}: Only PDF or JPEG files are allowed.`;
    if (file.size > 10 * 1024 * 1024)
      return `${label}: File size must be under 10 MB (selected: ${(file.size / 1024 / 1024).toFixed(1)} MB).`;
    return null;
  };

  // Save draft without marking complete
  const handleSaveDraft = async () => {
    setSubmitting(true);
    const filledFamily = familyMembers.filter(m => m.name.trim());
    try {
      const hasNewFiles = photo || docs.resume || docs.aadhar_copy || docs.pan_copy || docs.passbook_copy;
      if (hasNewFiles) {
        const fd = new FormData();
        if (photo) fd.append('profile_photo', photo);
        if (docs.resume) fd.append('resume', docs.resume);
        if (docs.aadhar_copy) fd.append('aadhar_copy', docs.aadhar_copy);
        if (docs.pan_copy) fd.append('pan_copy', docs.pan_copy);
        if (docs.passbook_copy) fd.append('passbook_copy', docs.passbook_copy);
        Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
        if (filledFamily.length > 0) fd.append('family_members', JSON.stringify(filledFamily));
        fd.append('_draft', 'true');
        await hrms.saveProfileDraft(fd);
      } else {
        await hrms.saveProfileDraft({ ...form, dob: form.dob || undefined, nominee_dob: form.nominee_dob || undefined, family_members: filledFamily.length > 0 ? JSON.stringify(filledFamily) : undefined });
      }
      alert('Draft saved successfully.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save draft.');
    } finally { setSubmitting(false); }
  };

  // Validate required fields and move to review step
  const handleReviewAndSubmit = () => {
    // Photo is mandatory
    if (!photo && !photoPreview) {
      setError('Profile photo is required. Please upload your photo.');
      window.scrollTo(0, 0);
      return;
    }

    // All 4 documents are mandatory + type/size validation
    const docChecks = [
      ['resume', 'resume_path', 'Resume'],
      ['aadhar_copy', 'aadhar_copy_path', 'Aadhar Copy'],
      ['pan_copy', 'pan_copy_path', 'PAN Copy'],
      ['passbook_copy', 'passbook_copy_path', 'Bank Passbook Front Page'],
    ];
    for (const [field, existingField, label] of docChecks) {
      if (!docs[field] && !existingDocs[existingField]) {
        setError(`"${label}" document is required.`);
        window.scrollTo(0, 0);
        return;
      }
      if (docs[field]) {
        const docErr = validateDocFile(docs[field], label);
        if (docErr) { setError(docErr); window.scrollTo(0, 0); return; }
      }
    }

    const required = [
      ['full_name', 'Name as per Aadhar'],
      ['phone', 'Primary Contact No'],
      ['personal_email', 'Personal Email'],
      ['emergency_contact', 'Emergency Contact Number'],
      ['dob', 'Date of Birth'],
      ['gender', 'Gender'],
      ['marital_status', 'Marital Status'],
      ['blood_group', 'Blood Group'],
      ['aadhar_number', 'Aadhar Number'],
      ['address', 'Permanent Address'],
      ['present_address', 'Present Address'],
      ['pan_number', 'PAN Number'],
      ['bank_ifsc', 'IFSC Code'],
      ['bank_name', 'Bank Name'],
      ['bank_account_number', 'Account Number'],
      ['nominee_name', 'Nominee Name'],
      ['nominee_relation', 'Nominee Relationship'],
    ];
    for (const [field, label] of required) {
      if (!form[field]?.trim()) {
        setError(`"${label}" is required.`);
        window.scrollTo(0, 0);
        return;
      }
    }

    // Education: at least one entry required
    if (education.length === 0) {
      setError('At least one Education entry is required.');
      window.scrollTo(0, 0);
      return;
    }

    // Phone: 10 digits, must start with 6–9
    if (!isValidPhone(form.phone)) {
      setError('Primary Contact No must be 10 digits and start with 6, 7, 8, or 9.');
      window.scrollTo(0, 0);
      return;
    }
    if (form.alternate_phone?.trim() && !isValidPhone(form.alternate_phone)) {
      setError('Alternate Contact No must be 10 digits and start with 6, 7, 8, or 9.');
      window.scrollTo(0, 0);
      return;
    }
    if (form.emergency_contact?.trim() && !isValidPhone(form.emergency_contact)) {
      setError('Emergency Contact must be 10 digits and start with 6, 7, 8, or 9.');
      window.scrollTo(0, 0);
      return;
    }
    if (form.nominee_contact?.trim() && !isValidPhone(form.nominee_contact)) {
      setError('Nominee Contact must be 10 digits and start with 6, 7, 8, or 9.');
      window.scrollTo(0, 0);
      return;
    }

    // PAN number format
    if (!isValidPAN(form.pan_number)) {
      setError('PAN Number format is invalid. Expected format: ABCDE1234F');
      window.scrollTo(0, 0);
      return;
    }

    // Bank account: numeric 9-18 digits
    if (!isValidAccountNumber(form.bank_account_number)) {
      setError('Account Number must be 9–18 digits (numbers only).');
      window.scrollTo(0, 0);
      return;
    }

    setError('');
    setStep('review');
    window.scrollTo(0, 0);
  };

  // Final submit
  const handleSubmit = async () => {
    setSubmitting(true);
    const filledFamily = familyMembers.filter(m => m.name.trim());
    try {
      // Always use multipart if any file (photo or doc) is being uploaded
      const hasNewFiles = photo || docs.resume || docs.aadhar_copy || docs.pan_copy || docs.passbook_copy;
      if (hasNewFiles) {
        const fd = new FormData();
        if (photo) fd.append('profile_photo', photo);
        if (docs.resume) fd.append('resume', docs.resume);
        if (docs.aadhar_copy) fd.append('aadhar_copy', docs.aadhar_copy);
        if (docs.pan_copy) fd.append('pan_copy', docs.pan_copy);
        if (docs.passbook_copy) fd.append('passbook_copy', docs.passbook_copy);
        Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
        if (filledFamily.length > 0) fd.append('family_members', JSON.stringify(filledFamily));
        await hrms.completeProfileWithPhoto(fd);
      } else {
        await hrms.completeProfile({
          ...form,
          dob: form.dob || undefined,
          nominee_dob: form.nominee_dob || undefined,
          family_members: filledFamily.length > 0 ? JSON.stringify(filledFamily) : undefined,
        });
      }
      await refreshUser();
      setStep('done');
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to submit profile. Please try again.');
      setStep('edit');
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) return <div style={{ padding: '2rem', color: theme.textMuted }}>Loading...</div>;

  // ── Done screen ───────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div style={{ maxWidth: '600px', margin: '4rem auto', textAlign: 'center', padding: '2rem' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✓</div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, marginBottom: '0.75rem' }}>Profile Submitted Successfully!</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.95rem', marginBottom: '2rem' }}>
          Your personal details have been saved and submitted. HR will review your profile.
        </p>
        <Button variant="primary" onClick={() => navigate('/employee/dashboard')}>Go to Dashboard</Button>
      </div>
    );
  }

  const bankLocked = ifscStatus === 'success';
  const ifscBorderColor = ifscStatus === 'success' ? theme.success : ifscStatus === 'error' ? theme.danger : theme.border;
  const filledFamily = familyMembers.filter(m => m.name.trim());
  const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  // ── Review screen ─────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div style={{ maxWidth: '960px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Review Your Profile</h1>
            <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
              Please review all details carefully before submitting. Click Back to make changes.
            </p>
          </div>
        </div>

        {profileRejectionReason && (
          <div style={{ background: '#FEF2F2', border: '2px solid #FECACA', borderLeft: '5px solid #DC2626', borderRadius: '0.5rem', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.3rem' }}>HR Rejection Remarks (Read-only)</span>
            <span style={{ fontSize: '0.875rem', color: '#374151', fontStyle: 'italic' }}>{profileRejectionReason}</span>
          </div>
        )}

        <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.875rem', color: '#92400E' }}>
          ⚠️ Once submitted, contact HR to make any changes to your profile.
        </div>

        {/* Profile Photo */}
        {photoPreview && (
          <Card title="Profile Photo" style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img src={photoPreview} alt="Profile" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${theme.border}` }} />
              <span style={{ fontSize: '0.875rem', color: theme.textMuted }}>Photo uploaded</span>
            </div>
          </Card>
        )}

        <ReviewSection title="Documents">
          {[
            { key: 'resume',        existingKey: 'resume_path',        label: 'Resume' },
            { key: 'aadhar_copy',   existingKey: 'aadhar_copy_path',   label: 'Aadhar Card Copy' },
            { key: 'pan_copy',      existingKey: 'pan_copy_path',      label: 'PAN Card Copy' },
            { key: 'passbook_copy', existingKey: 'passbook_copy_path', label: 'Bank Passbook Front Page' },
          ].map(({ key, existingKey, label }) => {
            const newFile = docs[key];
            const existingPath = existingDocs[existingKey];
            return (
              <div key={key} style={{ display: 'flex', padding: '0.45rem 0', borderBottom: `1px solid ${theme.border}`, alignItems: 'center' }}>
                <span style={{ width: '200px', flexShrink: 0, fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500 }}>{label}</span>
                {newFile
                  ? <span style={{ fontSize: '0.875rem', color: theme.success, fontWeight: 500 }}>✓ {newFile.name}</span>
                  : existingPath
                    ? <a href={getFileUrl(existingPath)} target="_blank" rel="noreferrer" style={{ fontSize: '0.875rem', color: theme.primary, fontWeight: 500, textDecoration: 'none' }}>✓ View Uploaded File ↗</a>
                    : <span style={{ fontSize: '0.875rem', color: theme.danger }}>Not uploaded</span>
                }
              </div>
            );
          })}
        </ReviewSection>

        <ReviewSection title="Personal Information">
          <ReviewRow label="Name as per Aadhar" value={form.full_name} />
          <ReviewRow label="Father / Husband Name" value={form.father_husband_name} />
          <ReviewRow label="Date of Birth" value={form.dob ? new Date(form.dob).toLocaleDateString('en-IN') : ''} />
          <ReviewRow label="Gender" value={capitalize(form.gender)} />
          <ReviewRow label="Marital Status" value={capitalize(form.marital_status)} />
          <ReviewRow label="Blood Group" value={form.blood_group} />
          <ReviewRow label="Qualification" value={form.qualification} />
          <ReviewRow label="Primary Contact" value={form.phone} />
          <ReviewRow label="Alternate Contact" value={form.alternate_phone} />
          <ReviewRow label="Personal Email" value={form.personal_email} />
          <ReviewRow label="Aadhar Number" value={form.aadhar_number} />
          <ReviewRow label="Permanent Address" value={form.address} />
          <ReviewRow label="Present Address" value={form.present_address} />
        </ReviewSection>

        <ReviewSection title="Bank Details">
          <ReviewRow label="IFSC Code" value={form.bank_ifsc} />
          <ReviewRow label="Bank Name" value={form.bank_name} />
          <ReviewRow label="Branch" value={form.bank_branch} />
          <ReviewRow label="Account Number" value={form.bank_account_number} />
          <ReviewRow label="PAN Number" value={form.pan_number} />
        </ReviewSection>

        {(hasPF === 'yes' || hasESI === 'yes') && (
          <ReviewSection title="Government & Compliance">
            {hasPF === 'yes' && <ReviewRow label="PF UAN" value={form.pf_uan} />}
            {hasESI === 'yes' && <ReviewRow label="ESI Number" value={form.esic_number} />}
            {hasESI === 'yes' && <ReviewRow label="ESI Hospital" value={form.esi_hospital} />}
          </ReviewSection>
        )}

        <ReviewSection title="Nominee Details">
          <ReviewRow label="Nominee Name" value={form.nominee_name} />
          <ReviewRow label="Relationship" value={form.nominee_relation} />
          <ReviewRow label="Nominee Aadhar" value={form.nominee_aadhar} />
          <ReviewRow label="Nominee DOB" value={form.nominee_dob ? new Date(form.nominee_dob).toLocaleDateString('en-IN') : ''} />
          <ReviewRow label="Nominee Contact" value={form.nominee_contact} />
        </ReviewSection>

        {filledFamily.length > 0 && (
          <Card title="Family Members" style={{ marginBottom: '1.25rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Name', 'Relation', 'Date of Birth', 'Aadhar'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.75rem', fontWeight: 600, fontSize: '0.8rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filledFamily.map((m, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '0.4rem 0.75rem' }}>{m.name || '—'}</td>
                    <td style={{ padding: '0.4rem 0.75rem' }}>{m.relation || '—'}</td>
                    <td style={{ padding: '0.4rem 0.75rem' }}>{m.dob ? new Date(m.dob).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding: '0.4rem 0.75rem' }}>{m.aadhar || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <ReviewSection title="Education">
          {education.length > 0 ? education.map((e, i) => (
            <ReviewRow key={i} label={e.degree} value={`${e.institution}${e.end_year ? ' · ' + e.end_year : ''}${e.field_of_study ? ' · ' + e.field_of_study : ''}`} />
          )) : <p style={{ fontSize: '0.8rem', color: theme.danger, margin: '0.25rem 0' }}>⚠️ No education entries added.</p>}
        </ReviewSection>

        {skills.length > 0 && (
          <Card title="Skills" style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.25rem' }}>
              {skills.map((s, i) => (
                <span key={i} style={{ background: theme.primaryLight, color: theme.primaryDark, padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 600 }}>
                  {s.skill_name} · {s.proficiency_level}
                </span>
              ))}
            </div>
          </Card>
        )}

        <ReviewSection title="Certifications">
          {certifications.length > 0 ? certifications.map((c, i) => (
            <ReviewRow key={i} label={c.name} value={`${c.issuing_org || ''}${c.issue_date ? ' · ' + c.issue_date : ''}`} />
          )) : <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.25rem 0' }}>None added.</p>}
        </ReviewSection>

        <Card title="Past Experience" style={{ marginBottom: '1.25rem' }}>
          {experiences.length > 0 ? experiences.map((exp, i) => {
            const fromStr = exp.from_date ? new Date(exp.from_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
            const toStr = exp.to_date ? new Date(exp.to_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present';
            return (
              <div key={i} style={{ display: 'flex', padding: '0.5rem 0', borderBottom: `1px solid ${theme.border}`, gap: '0.5rem' }}>
                <span style={{ fontSize: '1rem' }}>🏢</span>
                <div>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>{exp.designation} — {exp.company_name}</p>
                  <p style={{ margin: '0.1rem 0 0', fontSize: '0.77rem', color: theme.textMuted }}>{fromStr}{fromStr ? ' – ' : ''}{toStr}</p>
                </div>
              </div>
            );
          }) : <p style={{ fontSize: '0.8rem', color: theme.textMuted, padding: '0.5rem 0' }}>None added.</p>}
        </Card>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingBottom: '2rem', marginTop: '1rem' }}>
          <Button variant="outlineGray" onClick={() => { setStep('edit'); window.scrollTo(0, 0); }}>← Back</Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>Submit Profile</Button>
        </div>
      </div>
    );
  }

  // ── Edit screen ───────────────────────────────────────────────
  const isRejected = profileStatus === 'rejected';

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: isRejected ? '#DC2626' : theme.text, margin: 0 }}>
            {isRejected ? '✏️ Edit & Resubmit Profile' : 'Complete Your Profile'}
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            Fill in all details — fields marked <span style={{ color: theme.danger }}>*</span> are mandatory
          </p>
        </div>
      </div>

      {profileStatus === 'approved' && (
        <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: '#065F46', fontSize: '0.875rem', fontWeight: 500 }}>
          ✅ Your profile has been verified and approved by HR.
        </div>
      )}
      {profileStatus === 'pending_review' && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: '#92400E', fontSize: '0.875rem', fontWeight: 500 }}>
          🕐 Your profile has been submitted and is pending HR review.
        </div>
      )}
      {isRejected && (
        <div style={{ background: '#FEF2F2', border: '2px solid #FECACA', borderLeft: '5px solid #DC2626', borderRadius: '0.5rem', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.1rem' }}>❌</span>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#DC2626' }}>Profile Verification Rejected by HR</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
            Please review the remarks below, correct all highlighted issues, and resubmit for verification.
          </p>
          {profileRejectionReason && (
            <div style={{ marginTop: '0.75rem', background: '#fff', border: '1px solid #FECACA', borderRadius: '0.375rem', padding: '0.75rem 1rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.3rem' }}>HR Remarks (Read-only)</span>
              <span style={{ fontSize: '0.875rem', color: '#374151', fontStyle: 'italic' }}>{profileRejectionReason}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Profile Photo */}
      <Card title={<>Profile Photo <span style={{ color: theme.danger }}>*</span></>} style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: `2px solid ${photo || photoPreview ? theme.primary : theme.border}` }}>
            {photoPreview
              ? <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: '2rem', color: theme.primaryDark }}>👤</span>}
          </div>
          <div>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} id="cp-photo-upload" style={{ display: 'none' }} />
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('cp-photo-upload').click()}>
                {photoPreview ? 'Change Photo' : 'Upload Photo'}
              </Button>
              {photoPreview && (
                <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  style={{ background: 'none', border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: '0.375rem', padding: '0.3rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer' }}>
                  ✕ Remove
                </button>
              )}
            </div>
            <p style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.4rem' }}>JPG, PNG, WebP — max 10 MB. Required.</p>
          </div>
        </div>
      </Card>

      {/* Documents */}
      <Card title={<>Documents <span style={{ color: theme.danger }}>*</span></>} style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0 0 1rem' }}>All 4 documents are mandatory. Accepted formats: PDF or JPEG only. Max 10 MB each.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
          {[
            { key: 'resume',        existingKey: 'resume_path',        label: 'Resume',                    accept: '.pdf,.jpg,.jpeg', icon: '📄' },
            { key: 'aadhar_copy',   existingKey: 'aadhar_copy_path',   label: 'Aadhar Card Copy',          accept: '.pdf,.jpg,.jpeg', icon: '🪪' },
            { key: 'pan_copy',      existingKey: 'pan_copy_path',      label: 'PAN Card Copy',             accept: '.pdf,.jpg,.jpeg', icon: '💳' },
            { key: 'passbook_copy', existingKey: 'passbook_copy_path', label: 'Bank Passbook Front Page',  accept: '.pdf,.jpg,.jpeg', icon: '🏦' },
          ].map(({ key, existingKey, label, accept, icon }) => {
            const hasExisting = !!existingDocs[existingKey];
            const newFile = docs[key];
            return (
              <div key={key} style={{ border: `1.5px solid ${newFile || hasExisting ? theme.success : theme.border}`, borderRadius: '0.5rem', padding: '0.875rem', background: newFile || hasExisting ? '#F0FDF4' : '#FAFAFA' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>{icon}</span>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.text }}>{label} <span style={{ color: theme.danger }}>*</span></label>
                </div>
                {(newFile || hasExisting) && (
                  <p style={{ fontSize: '0.75rem', color: theme.success, margin: '0 0 0.5rem', fontWeight: 500 }}>
                    {newFile ? `✓ ${newFile.name}` : '✓ Already uploaded'}
                  </p>
                )}
                <input
                  type="file" accept={accept}
                  id={`doc-${key}`} style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files[0]; if (f) setDocs(p => ({ ...p, [key]: f })); }}
                />
                <button type="button"
                  onClick={() => document.getElementById(`doc-${key}`).click()}
                  style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', background: '#fff', cursor: 'pointer', color: theme.text }}>
                  {newFile || hasExisting ? 'Replace' : 'Upload'}
                </button>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Personal Information */}
      <Section title="Personal Information">
        <Field label="Name as per Aadhar" required fullWidth>
          <input style={{ ...inp, fontWeight: 600, fontSize: '1rem' }} name="full_name" value={form.full_name} onChange={handleChange} placeholder="Full name exactly as printed on your Aadhar card" />
          <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.3rem 0 0' }}>This will be used as your official name in the application.</p>
        </Field>
        <Field label="Father / Husband Name">
          <input style={inp} name="father_husband_name" value={form.father_husband_name} onChange={handleChange} placeholder="Father or husband name" />
        </Field>
        <Field label="Date of Birth" required>
          <input style={inp} name="dob" type="date" value={form.dob} onChange={handleChange} max={today} />
        </Field>
        <Field label="Gender" required>
          <select style={inp} name="gender" value={form.gender} onChange={handleChange}>
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Marital Status" required>
          <select style={inp} name="marital_status" value={form.marital_status} onChange={handleChange}>
            <option value="">Select marital status</option>
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="divorced">Divorced</option>
            <option value="widowed">Widowed</option>
          </select>
        </Field>
        <Field label="Blood Group" required>
          <select style={inp} name="blood_group" value={form.blood_group} onChange={handleChange}>
            <option value="">Select blood group</option>
            {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
          </select>
        </Field>
        <Field label="Qualification">
          <input style={inp} name="qualification" value={form.qualification} onChange={handleChange} placeholder="e.g. B.E, MBA, 12th" />
        </Field>
        <Field label="Primary Contact No" required>
          <input style={inp} name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="9XXXXXXXXX" maxLength={10} inputMode="numeric" />
        </Field>
        <Field label="Alternate Contact No">
          <input style={inp} name="alternate_phone" type="tel" value={form.alternate_phone} onChange={handleChange} placeholder="9XXXXXXXXX" maxLength={10} inputMode="numeric" />
        </Field>
        <Field label="Emergency Contact Number" required>
          <input style={inp} name="emergency_contact" type="tel" value={form.emergency_contact} onChange={handleChange} placeholder="9XXXXXXXXX" maxLength={10} inputMode="numeric" />
        </Field>
        <Field label="Personal Email" required>
          <input style={inp} name="personal_email" type="email" value={form.personal_email} onChange={handleChange} placeholder="personal@email.com" />
        </Field>
        <Field label="Aadhar Number" required>
          <input style={inp} name="aadhar_number" value={form.aadhar_number} onChange={handleChange} placeholder="XXXX XXXX XXXX" maxLength={14} />
        </Field>
        <Field label="Permanent Address" required fullWidth>
          <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} name="address" value={form.address} onChange={handleChange} placeholder="Door No, Street, City, State, PIN" />
        </Field>
        <Field label="Present Address" required fullWidth>
          <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} name="present_address" value={form.present_address} onChange={handleChange} placeholder="Door No, Street, City, State, PIN" />
        </Field>
      </Section>

      {/* Bank Details */}
      <Card title="Bank Details" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
          <Field label="IFSC Code" required>
            <div style={{ position: 'relative' }}>
              <input style={{ ...inp, borderColor: ifscBorderColor, paddingRight: '2.5rem', textTransform: 'uppercase' }}
                name="bank_ifsc" value={form.bank_ifsc} onChange={handleIfscChange} placeholder="e.g. SBIN0001234" maxLength={11} />
              {ifscStatus === 'loading' && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: theme.textMuted, fontSize: 12 }}>...</span>}
              {ifscStatus === 'success' && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: theme.success }}>✓</span>}
              {ifscStatus === 'error' && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: theme.danger }}>✗</span>}
            </div>
            {ifscStatus === 'error' && <p style={{ fontSize: '0.75rem', color: theme.danger, marginTop: 4 }}>Invalid IFSC code</p>}
            {ifscStatus === 'success' && <p style={{ fontSize: '0.75rem', color: theme.success, marginTop: 4 }}>Bank details fetched automatically</p>}
          </Field>
          <Field label="Bank Name" required>
            <input style={{ ...inp, background: bankLocked ? '#f0fdf4' : theme.white, color: bankLocked ? theme.textMuted : theme.text }}
              name="bank_name" value={form.bank_name}
              onChange={bankLocked ? undefined : handleChange}
              readOnly={bankLocked} placeholder="Auto-filled from IFSC" />
          </Field>
          <Field label="Branch">
            <input style={{ ...inp, background: bankLocked ? '#f0fdf4' : theme.white, color: bankLocked ? theme.textMuted : theme.text }}
              name="bank_branch" value={form.bank_branch}
              onChange={bankLocked ? undefined : handleChange}
              readOnly={bankLocked} placeholder="Auto-filled from IFSC" />
          </Field>
          <Field label="Account Number" required>
            <input style={inp} name="bank_account_number" value={form.bank_account_number}
              onChange={e => setForm(p => ({ ...p, bank_account_number: e.target.value.replace(/\D/g, '') }))}
              placeholder="Account number (digits only)" inputMode="numeric" maxLength={18} />
            {form.bank_account_number && !isValidAccountNumber(form.bank_account_number) && (
              <p style={{ fontSize: '0.72rem', color: theme.danger, marginTop: 3 }}>Must be 9–18 digits, numbers only</p>
            )}
          </Field>
          <Field label="PAN Number" required>
            <input style={{ ...inp, textTransform: 'uppercase' }} name="pan_number" value={form.pan_number}
              onChange={e => setForm(p => ({ ...p, pan_number: e.target.value.toUpperCase() }))}
              placeholder="ABCDE1234F" maxLength={10} />
            {form.pan_number && !isValidPAN(form.pan_number) && (
              <p style={{ fontSize: '0.72rem', color: theme.danger, marginTop: 3 }}>Format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)</p>
            )}
          </Field>
        </div>
      </Card>

      {/* Government & Compliance */}
      <Card title="Government & Compliance" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
          <Field label="Do you have a PF Account (UAN)?">
            <div style={{ display: 'flex', gap: '1rem' }}>
              {['yes', 'no'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: hasPF === opt ? 700 : 400, color: hasPF === opt ? theme.primary : theme.text }}>
                  <input type="radio" name="hasPF" value={opt} checked={hasPF === opt}
                    onChange={() => { setHasPF(opt); if (opt === 'no') setForm(p => ({ ...p, pf_uan: '' })); }}
                    style={{ accentColor: theme.primary }} />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </Field>
          {hasPF === 'yes' && (
            <Field label="PF Account (UAN No)">
              <input style={inp} name="pf_uan" value={form.pf_uan} onChange={handleChange} placeholder="UAN number" />
            </Field>
          )}
          <Field label="Do you have an ESI Number?">
            <div style={{ display: 'flex', gap: '1rem' }}>
              {['yes', 'no'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: hasESI === opt ? 700 : 400, color: hasESI === opt ? theme.primary : theme.text }}>
                  <input type="radio" name="hasESI" value={opt} checked={hasESI === opt}
                    onChange={() => { setHasESI(opt); if (opt === 'no') setForm(p => ({ ...p, esic_number: '', esi_hospital: '' })); }}
                    style={{ accentColor: theme.primary }} />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </Field>
          {hasESI === 'yes' && (
            <>
              <Field label="ESI Number">
                <input style={inp} name="esic_number" value={form.esic_number} onChange={handleChange} placeholder="ESI number" />
              </Field>
              <Field label="ESI Hospital Location">
                <input style={inp} name="esi_hospital" value={form.esi_hospital} onChange={handleChange} placeholder="Nearest ESI hospital" />
              </Field>
            </>
          )}
        </div>
      </Card>

      {/* Nominee Details */}
      <Section title="Nominee Details">
        <Field label="Nominee Name" required>
          <input style={inp} name="nominee_name" value={form.nominee_name} onChange={handleChange} placeholder="Full name" />
        </Field>
        <Field label="Relationship" required>
          <select style={inp} name="nominee_relation" value={form.nominee_relation} onChange={handleChange}>
            <option value="">Select relationship…</option>
            {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Nominee Aadhar">
          <input style={inp} name="nominee_aadhar" value={form.nominee_aadhar} onChange={handleChange} placeholder="XXXX XXXX XXXX" maxLength={14} />
        </Field>
        <Field label="Nominee Date of Birth">
          <input style={inp} name="nominee_dob" type="date" value={form.nominee_dob} onChange={handleChange} />
        </Field>
        <Field label="Nominee Contact Number">
          <input style={inp} name="nominee_contact" type="tel" value={form.nominee_contact} onChange={handleChange} placeholder="9XXXXXXXXX" maxLength={13} />
        </Field>
      </Section>

      {/* Family Members */}
      <Card title="Family Members" style={{ marginBottom: '1.5rem' }}>
        {familyMembers.map((member, idx) => (
          <div key={idx} style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: '1rem', marginBottom: '0.75rem', background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>Family Member {idx + 1}</span>
              {familyMembers.length > 1 && (
                <button type="button" onClick={() => setFamilyMembers(prev => prev.filter((_, i) => i !== idx))}
                  style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 13 }}>Remove</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {[['name', 'Name *', 'Full name'], ['aadhar', 'Aadhar Number', 'XXXX XXXX XXXX']].map(([field, lbl, ph]) => (
                <div key={field}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>{lbl}</label>
                  <input style={inp} value={member[field]} onChange={e => updateMember(idx, field, e.target.value)} placeholder={ph} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Relationship *</label>
                <select style={inp} value={member.relation} onChange={e => updateMember(idx, 'relation', e.target.value)}>
                  <option value="">Select…</option>
                  {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Date of Birth</label>
                <input style={inp} type="date" value={member.dob} onChange={e => updateMember(idx, 'dob', e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        {familyMembers.length < 6 && (
          <button type="button" onClick={() => setFamilyMembers(prev => [...prev, { ...EMPTY_MEMBER }])}
            style={{ background: 'none', border: `1.5px dashed ${theme.border}`, borderRadius: 8, padding: '0.6rem 1.2rem', color: theme.primary, cursor: 'pointer', fontSize: '0.875rem', width: '100%', marginTop: 4 }}>
            + Add Family Member ({familyMembers.length}/6)
          </button>
        )}
      </Card>

      {/* Education */}
      <Card title="Education" style={{ marginBottom: '1.5rem' }}>
        {education.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            {education.map((e) => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>{e.degree}{e.field_of_study ? ` — ${e.field_of_study}` : ''}</p>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{e.institution}{e.end_year ? ` · ${e.end_year}` : ''}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button type="button" onClick={() => editEducation(e)} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.72rem', color: theme.textMuted }}>Edit</button>
                  <button type="button" onClick={() => removeEducation(e.id)} style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div id="edu-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', padding: '0.75rem', background: editingEduId ? '#FFFBEB' : '#f9fafb', borderRadius: 8, border: `1px dashed ${editingEduId ? '#FCD34D' : theme.border}` }}>
          {editingEduId && <div style={{ gridColumn: '1/-1', fontSize: '0.8rem', fontWeight: 600, color: '#92400e' }}>✏️ Editing — make changes and click Update</div>}
          {[['degree', 'Degree *', 'B.Tech / MBA...'], ['institution', 'Institution *', 'University name'], ['field_of_study', 'Field of Study', 'Computer Science']].map(([f, l, p]) => (
            <div key={f}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>{l}</label>
              <input style={inp} value={newEdu[f]} onChange={e => setNewEdu(p2 => ({ ...p2, [f]: e.target.value }))} placeholder={p} />
            </div>
          ))}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Graduation Year</label>
            <select style={inp} value={newEdu.end_year} onChange={e => setNewEdu(p => ({ ...p, end_year: e.target.value }))}>
              <option value="">Select year</option>
              {Array.from({ length: new Date().getFullYear() - 1969 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.4rem' }}>
            <Button type="button" variant="outline" size="sm" onClick={saveEducation}>{editingEduId ? '✓ Update' : '+ Add'}</Button>
            {editingEduId && <Button type="button" variant="outlineGray" size="sm" onClick={() => { setEditingEduId(null); setNewEdu({ degree: '', institution: '', field_of_study: '', end_year: '' }); }}>Cancel</Button>}
          </div>
        </div>
      </Card>

      {/* Skills */}
      <Card title="Skills" style={{ marginBottom: '1.5rem' }}>
        {skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {skills.map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: theme.primaryLight, border: `1px solid ${theme.primary}30`, borderRadius: '9999px', padding: '0.3rem 0.875rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.primaryDark }}>{s.skill_name}</span>
                <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>· {s.proficiency_level}</span>
                <button type="button" onClick={() => removeSkill(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: '0.75rem', padding: 0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: 8, border: `1px dashed ${theme.border}` }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Skill Name *</label>
            <input style={inp} value={newSkill.skill_name} onChange={e => setNewSkill(p => ({ ...p, skill_name: e.target.value }))} placeholder="React, Python..." />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Level</label>
            <select style={inp} value={newSkill.proficiency_level} onChange={e => setNewSkill(p => ({ ...p, proficiency_level: e.target.value }))}>
              {SKILL_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button type="button" variant="outline" size="sm" onClick={addSkill}>+ Add</Button>
          </div>
        </div>
      </Card>

      {/* Certifications */}
      <Card title="Certifications" style={{ marginBottom: '1.5rem' }}>
        {certifications.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            {certifications.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>{c.name}</p>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>{c.issuing_org}{c.issue_date ? ` · ${c.issue_date}` : ''}</p>
                </div>
                <button type="button" onClick={() => removeCert(c.id)} style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: 8, border: `1px dashed ${theme.border}` }}>
          {[['name', 'Certification Name *', 'AWS Solutions...'], ['issuing_org', 'Issuing Organization', 'Amazon Web Services']].map(([f, l, p]) => (
            <div key={f}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>{l}</label>
              <input style={inp} value={newCert[f]} onChange={e => setNewCert(p2 => ({ ...p2, [f]: e.target.value }))} placeholder={p} />
            </div>
          ))}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Issue Date</label>
            <input style={inp} type="date" value={newCert.issue_date} onChange={e => setNewCert(p => ({ ...p, issue_date: e.target.value }))} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Expiry Date</label>
            <input style={inp} type="date" value={newCert.expiry_date} onChange={e => setNewCert(p => ({ ...p, expiry_date: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button type="button" variant="outline" size="sm" onClick={addCert}>+ Add</Button>
          </div>
        </div>
      </Card>

      {/* Past Experience */}
      <Card title="Past Experience" style={{ marginBottom: '1.5rem' }}>
        {/* LinkedIn-style experience list */}
        {experiences.length > 0 && (
          <div style={{ marginBottom: '1.25rem', position: 'relative' }}>
            {/* vertical timeline line */}
            <div style={{ position: 'absolute', left: 19, top: 24, bottom: 8, width: 2, background: theme.border }} />
            {experiences.map((exp) => {
              const fromStr = exp.from_date ? new Date(exp.from_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
              const toStr = exp.to_date ? new Date(exp.to_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present';
              let duration = '';
              if (exp.from_date) {
                const start = new Date(exp.from_date);
                const end = exp.to_date ? new Date(exp.to_date) : new Date();
                const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                const yrs = Math.floor(months / 12);
                const mos = months % 12;
                duration = yrs > 0 ? `${yrs} yr${yrs > 1 ? 's' : ''}${mos > 0 ? ` ${mos} mo` : ''}` : `${mos} mo`;
              }
              return (
                <div key={exp.id} style={{ display: 'flex', gap: '0.875rem', paddingBottom: '1.25rem', position: 'relative' }}>
                  {/* company icon */}
                  <div style={{ width: 40, height: 40, borderRadius: '0.5rem', background: '#EFF6FF', border: `1px solid #BFDBFE`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0, zIndex: 1 }}>🏢</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>{exp.designation}</p>
                        <p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.82rem', color: theme.primary }}>{exp.company_name}</p>
                        <p style={{ margin: '0.1rem 0 0', fontSize: '0.77rem', color: theme.textMuted }}>
                          {fromStr}{fromStr ? ' – ' : ''}{toStr}{duration ? ` · ${duration}` : ''}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                        <button type="button" onClick={() => editExp(exp)} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.72rem', color: theme.textMuted }}>Edit</button>
                        <button type="button" onClick={() => removeExp(exp.id)} style={{ background: 'none', border: `1px solid ${theme.danger}40`, borderRadius: '0.375rem', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.72rem', color: theme.danger }}>✕</button>
                      </div>
                    </div>
                    {exp.description && (
                      <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: theme.gray, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{exp.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add / Edit form */}
        <div id="exp-form" style={{ padding: '0.875rem', background: editingExpId ? '#FFFBEB' : '#f9fafb', borderRadius: 8, border: `1px dashed ${editingExpId ? theme.warning : theme.border}` }}>
          {editingExpId && <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: '#92400e' }}>✏️ Editing experience — make changes below</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Company Name *</label>
              <input style={inp} value={newExp.company_name} onChange={e => setNewExp(p => ({ ...p, company_name: e.target.value }))} placeholder="Google, Infosys..." />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Designation / Title *</label>
              <input style={inp} value={newExp.designation} onChange={e => setNewExp(p => ({ ...p, designation: e.target.value }))} placeholder="Software Engineer..." />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>From Date</label>
              <input style={inp} type="date" value={newExp.from_date} onChange={e => setNewExp(p => ({ ...p, from_date: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>To Date</label>
              <input style={inp} type="date" value={newExp.to_date} onChange={e => setNewExp(p => ({ ...p, to_date: e.target.value }))} disabled={newExp.is_current} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: 5, cursor: 'pointer', fontSize: '0.77rem', color: theme.textMuted }}>
                <input type="checkbox" checked={newExp.is_current} onChange={e => setNewExp(p => ({ ...p, is_current: e.target.checked, to_date: e.target.checked ? '' : p.to_date }))} />
                Currently working here
              </label>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.textMuted, marginBottom: 4 }}>Role Description</label>
              <textarea style={{ ...inp, minHeight: 72, resize: 'vertical' }} value={newExp.description} onChange={e => setNewExp(p => ({ ...p, description: e.target.value }))} placeholder="Describe your key responsibilities and achievements..." />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <Button type="button" variant="outline" size="sm" onClick={saveExp}>{editingExpId ? '✓ Update Experience' : '+ Add Experience'}</Button>
            {editingExpId && (
              <Button type="button" variant="outlineGray" size="sm" onClick={() => { setEditingExpId(null); setNewExp({ ...EMPTY_EXP }); }}>Cancel</Button>
            )}
          </div>
        </div>
      </Card>

      {/* Bottom action */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingBottom: '2rem', flexWrap: 'wrap' }}>
        <Button type="button" variant="outlineGray" onClick={() => navigate('/employee/dashboard')}>Cancel</Button>
        <Button type="button" variant="outline" onClick={handleSaveDraft} loading={submitting}>Save Draft</Button>
        <Button type="button" variant="primary" onClick={handleReviewAndSubmit}>Review & Submit →</Button>
      </div>
    </div>
  );
}
